import { useCallback, useEffect, useMemo, useState } from 'react';

import formatTime from '@cartwhl/common/utils/formatTime';
import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import useMediaQuery from '@cartwhl/ui/hooks/useMediaQuery';
import PlusIcon from '@cartwhl/ui/icons/PlusIcon';
import Button from '@cartwhl/ui/input/Button';
import MobileTrimBar from '@cartwhl/ui/organisms/MobileTrimBar';
import TrimBar from '@cartwhl/ui/organisms/TrimBar';

import { ProgressInfo, VideoEditSettings } from 'hooks/useFfmpeg';
import { noticesStore } from 'stores';

import { EditableVideo } from '../types';
import VideoPlayer from './CustomVideoPlayer';
import EditModeButtons from './EditModeButtons';
import LoadingOverlay from './LoadingOverlay';

export default function Trimming({
  defaultEditSettings,
  close,
  next,
  openCropping,
  mobileTrimbarMode,
  intermediateNext,
  intermediateBack,
  inputVideo,
  hasExistingJobData,
  progress,
  thumbnails,
  saveOriginal,
  setSaveOriginal,
}: {
  defaultEditSettings?: VideoEditSettings;
  close: () => void;
  openCropping: (trimValues: [number, number]) => void;
  mobileTrimbarMode: 'start' | 'end';
  intermediateNext: () => void;
  intermediateBack: () => void;
  next: (
    trimValues: [number, number],
    clearJobData?: boolean,
  ) => void | Promise<void>;
  inputVideo: EditableVideo | null;
  hasExistingJobData: boolean;
  progress: ProgressInfo | null;
  thumbnails?: string[];
  saveOriginal: boolean;
  setSaveOriginal: (saveOriginal: boolean) => void;
}) {
  const [trimValues, setTrimValues] = useState<[number, number] | undefined>();
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isMobile = !isDesktop;
  const addText = isMobile ? 'Add' : 'Add motion';
  const useText = isMobile ? 'Use new' : 'Use as new motion';

  useEffect(() => {
    if (!inputVideo) return;
    // Set initial trim values
    setTrimValues(
      defaultEditSettings?.trim || [0, inputVideo.metadata.duration],
    );
  }, [inputVideo, defaultEditSettings]);

  const videoDuration = trimValues ? trimValues[1] - trimValues[0] : 0;

  const videoUrl = useMemo(() => {
    if (inputVideo) return URL.createObjectURL(inputVideo.blob);
  }, [inputVideo]);

  useEffect(() => {
    // let the browser know not to keep the reference to the file any longer
    return () => {
      if (videoUrl) URL.revokeObjectURL(videoUrl);
    };
  }, [videoUrl]);

  const handleVideoSubmission = async (clearJobData: boolean) => {
    if (!inputVideo || progress) {
      noticesStore.error('Please wait for the video to be ready');
      return;
    }
    if (videoDuration > 30 || videoDuration < 1) {
      noticesStore.error(
        'Please keep the video anywhere between 1 and 30 seconds',
      );
      return;
    }
    if (!trimValues) {
      noticesStore.error('Something went wrong');
      console.error('Trimming: There should be trim values');
      return;
    }
    next(trimValues, clearJobData);
  };

  const onTrimChange = useCallback((s: number, e: number) => {
    setTrimValues([s, e]);
  }, []);

  return (
    <div className="flex h-full flex-col">
      <Text
        as="p"
        variant="studio/body/sm"
        className="text-gray-60 dark:text-gray-45 -mt-0.5 mb-2.5 flex-none lg:hidden"
      >
        Select where your video will{' '}
        <strong className="text-gray-90 dark:text-white">
          {mobileTrimbarMode}
        </strong>{' '}
        - your final clip can be between{' '}
        <strong className="text-gray-90 dark:text-white">
          1 and 30 seconds
        </strong>{' '}
        long.
      </Text>
      <div className="bg-gray-15 dark:bg-gray-75 dark:border-gray-60 border-gray-30 relative flex min-h-0 flex-1 flex-col rounded-lg border">
        {progress && <LoadingOverlay progress={progress} />}
        <div className="bg-gray-90 relative flex h-full min-h-0 w-full flex-1 items-center justify-center overflow-hidden rounded-t-lg md:aspect-video dark:bg-black">
          {videoUrl && trimValues && inputVideo && (
            <>
              <VideoPlayer
                className="h-full w-full"
                src={videoUrl}
                mimeType={inputVideo?.mimeType ?? ''}
                startTime={trimValues[0]}
                endTime={trimValues[1]}
                duration={inputVideo.metadata.duration}
                currentTime={currentTime}
                onTimeUpdate={setCurrentTime}
                objectFit={'contain'}
                isPlaying={isPlaying}
                setIsPlaying={setIsPlaying}
              />

              <EditModeButtons
                editMode="trim"
                onClick={() => openCropping(trimValues)}
              />

              <Info
                minGap={1}
                maxGap={30}
                duration={trimValues[1] - trimValues[0]}
              />
            </>
          )}
        </div>
        <div className="flex flex-col items-end justify-center gap-2 py-2 sm:flex-row sm:items-center sm:gap-3 md:px-5 lg:h-[85px]">
          <div className="min-w-0 flex-1 self-stretch lg:self-auto">
            {inputVideo && (
              <>
                {isDesktop ? (
                  <TrimBar
                    value={currentTime}
                    minGap={1}
                    maxGap={30}
                    maxValue={inputVideo.metadata.duration}
                    trimStart={trimValues?.[0] || 0}
                    trimEnd={trimValues?.[1] || inputVideo.metadata.duration}
                    onValueChange={setCurrentTime}
                    onTrimChange={onTrimChange}
                    color="violet"
                    thumbnails={thumbnails}
                    format={formatTime}
                  />
                ) : (
                  <MobileTrimBar
                    mode={mobileTrimbarMode}
                    value={currentTime}
                    maxValue={inputVideo.metadata.duration}
                    trimStart={trimValues?.[0] || 0}
                    trimEnd={trimValues?.[1] || inputVideo.metadata.duration}
                    onValueChange={setCurrentTime}
                    onTrimChange={onTrimChange}
                    thumbnails={thumbnails}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-none flex-wrap items-center justify-between gap-4 md:mt-6 md:gap-8">
        <Button onClick={close} className="order-1 sm:order-0">
          Cancel
        </Button>
        <Checkbox
          id="save_original_checkbox"
          checked={saveOriginal}
          setChecked={() => setSaveOriginal(!saveOriginal)}
          label="Save video for comparison"
          variant="checkbox"
          color="settings"
          className="w-full flex-none sm:ml-auto sm:w-auto sm:flex-initial"
        />

        <div className="order-2 flex gap-2 sm:order-0">
          {isMobile && mobileTrimbarMode === 'end' && (
            <Button className="sm:hidden" onClick={intermediateBack}>
              Back
            </Button>
          )}

          {isMobile ? (
            <>
              <Button variant="primary" onClick={intermediateNext}>
                Next
              </Button>
            </>
          ) : (
            <>
              {hasExistingJobData && (
                <Button
                  className="mr-2"
                  variant="secondary"
                  onClick={() => handleVideoSubmission(true)}
                >
                  {useText}
                </Button>
              )}
              <Tooltip
                text="Please keep the video anywhere between 1 and 30 seconds"
                disabled={videoDuration <= 30 && videoDuration > 1}
                inModal
              >
                <Button
                  variant="primary"
                  icon={<PlusIcon />}
                  onClick={() => handleVideoSubmission(false)}
                >
                  {addText}
                </Button>
              </Tooltip>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Info = ({
  duration,
  minGap,
  maxGap,
}: {
  duration: number;
  minGap: number;
  maxGap: number;
}) => {
  const isValid = duration >= minGap && duration <= maxGap;
  return (
    <div
      className={`absolute top-2 right-2 flex h-[36px] items-center gap-1.5 rounded-full pr-3 pl-2 ${
        isValid ? 'bg-gray-90/80' : 'bg-red'
      }`}
    >
      {isValid ? (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          className="fill-green"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M9.9917 18.5815C8.81852 18.5815 7.71452 18.3574 6.67969 17.9092C5.64486 17.4665 4.73177 16.8494 3.94043 16.0581C3.15462 15.2668 2.5376 14.3537 2.08936 13.3188C1.64111 12.284 1.41699 11.18 1.41699 10.0068C1.41699 8.83366 1.64111 7.72965 2.08936 6.69482C2.5376 5.65999 3.15462 4.74967 3.94043 3.96387C4.73177 3.17253 5.64209 2.55273 6.67139 2.10449C7.70622 1.65625 8.81022 1.43213 9.9834 1.43213C11.1621 1.43213 12.2689 1.65625 13.3037 2.10449C14.3385 2.55273 15.2516 3.17253 16.043 3.96387C16.8343 4.74967 17.4541 5.65999 17.9023 6.69482C18.3506 7.72965 18.5747 8.83366 18.5747 10.0068C18.5747 11.18 18.3506 12.284 17.9023 13.3188C17.4541 14.3537 16.8343 15.2668 16.043 16.0581C15.2516 16.8494 14.3385 17.4665 13.3037 17.9092C12.2689 18.3574 11.1649 18.5815 9.9917 18.5815ZM9.9917 16.8882C10.9491 16.8882 11.8428 16.7111 12.6729 16.3569C13.5085 16.0028 14.2389 15.5103 14.8643 14.8794C15.4951 14.2485 15.9876 13.5181 16.3418 12.688C16.696 11.8579 16.873 10.9642 16.873 10.0068C16.873 9.05501 16.696 8.16406 16.3418 7.33398C15.9876 6.49837 15.4924 5.76514 14.856 5.13428C14.2251 4.50342 13.4946 4.0109 12.6646 3.65674C11.8345 3.30257 10.9408 3.12549 9.9834 3.12549C9.03158 3.12549 8.13786 3.30257 7.30225 3.65674C6.47217 4.0109 5.74447 4.50342 5.11914 5.13428C4.49382 5.76514 4.00407 6.49837 3.6499 7.33398C3.29574 8.16406 3.11865 9.05501 3.11865 10.0068C3.11865 10.9642 3.29574 11.8579 3.6499 12.688C4.00407 13.5181 4.49382 14.2485 5.11914 14.8794C5.75 15.5103 6.48047 16.0028 7.31055 16.3569C8.14616 16.7111 9.03988 16.8882 9.9917 16.8882ZM9.09521 13.9497C8.94027 13.9497 8.80192 13.9165 8.68018 13.8501C8.55843 13.7782 8.43945 13.673 8.32324 13.5347L6.36426 11.144C6.22038 10.9614 6.14844 10.776 6.14844 10.5879C6.14844 10.3831 6.21761 10.2088 6.35596 10.0649C6.49984 9.91553 6.67139 9.84082 6.87061 9.84082C6.99788 9.84082 7.11133 9.86849 7.21094 9.92383C7.31608 9.97363 7.41846 10.0649 7.51807 10.1978L9.06201 12.1567L12.3408 6.90234C12.5124 6.62012 12.7282 6.479 12.9883 6.479C13.182 6.479 13.3563 6.54264 13.5112 6.66992C13.6662 6.7972 13.7437 6.96322 13.7437 7.16797C13.7437 7.26204 13.7215 7.35889 13.6772 7.4585C13.6385 7.55257 13.5915 7.64111 13.5361 7.72412L9.82568 13.5264C9.6486 13.8086 9.40511 13.9497 9.09521 13.9497Z" />
        </svg>
      ) : (
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="white"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M9.9917 18.5815C8.81852 18.5815 7.71452 18.3574 6.67969 17.9092C5.64486 17.4665 4.73177 16.8494 3.94043 16.0581C3.15462 15.2668 2.5376 14.3537 2.08936 13.3188C1.64111 12.284 1.41699 11.18 1.41699 10.0068C1.41699 8.83366 1.64111 7.72965 2.08936 6.69482C2.5376 5.65999 3.15462 4.74967 3.94043 3.96387C4.73177 3.17253 5.64209 2.55273 6.67139 2.10449C7.70622 1.65625 8.81022 1.43213 9.9834 1.43213C11.1621 1.43213 12.2689 1.65625 13.3037 2.10449C14.3385 2.55273 15.2516 3.17253 16.043 3.96387C16.8343 4.74967 17.4541 5.65999 17.9023 6.69482C18.3506 7.72965 18.5747 8.83366 18.5747 10.0068C18.5747 11.18 18.3506 12.284 17.9023 13.3188C17.4541 14.3537 16.8343 15.2668 16.043 16.0581C15.2516 16.8494 14.3385 17.4665 13.3037 17.9092C12.2689 18.3574 11.1649 18.5815 9.9917 18.5815ZM9.9917 16.8882C10.9491 16.8882 11.8428 16.7111 12.6729 16.3569C13.5085 16.0028 14.2389 15.5103 14.8643 14.8794C15.4951 14.2485 15.9876 13.5181 16.3418 12.688C16.696 11.8579 16.873 10.9642 16.873 10.0068C16.873 9.05501 16.696 8.16406 16.3418 7.33398C15.9876 6.49837 15.4924 5.76514 14.856 5.13428C14.2251 4.50342 13.4946 4.0109 12.6646 3.65674C11.8345 3.30257 10.9408 3.12549 9.9834 3.12549C9.03158 3.12549 8.13786 3.30257 7.30225 3.65674C6.47217 4.0109 5.74447 4.50342 5.11914 5.13428C4.49382 5.76514 4.00407 6.49837 3.6499 7.33398C3.29574 8.16406 3.11865 9.05501 3.11865 10.0068C3.11865 10.9642 3.29574 11.8579 3.6499 12.688C4.00407 13.5181 4.49382 14.2485 5.11914 14.8794C5.75 15.5103 6.48047 16.0028 7.31055 16.3569C8.14616 16.7111 9.03988 16.8882 9.9917 16.8882ZM9.9917 11.3848C9.51025 11.3848 9.26123 11.1413 9.24463 10.6543L9.12842 6.62012C9.11735 6.37663 9.19206 6.17741 9.35254 6.02246C9.51302 5.86751 9.72054 5.79004 9.9751 5.79004C10.2297 5.79004 10.4399 5.87028 10.606 6.03076C10.772 6.18571 10.8494 6.38216 10.8384 6.62012L10.7056 10.6543C10.6945 11.1413 10.4565 11.3848 9.9917 11.3848ZM9.9917 14.1406C9.72054 14.1406 9.48812 14.0549 9.29443 13.8833C9.10628 13.7062 9.01221 13.4876 9.01221 13.2275C9.01221 12.9674 9.10628 12.7516 9.29443 12.5801C9.48812 12.403 9.72054 12.3145 9.9917 12.3145C10.2573 12.3145 10.4842 12.4002 10.6724 12.5718C10.866 12.7433 10.9629 12.9619 10.9629 13.2275C10.9629 13.4932 10.866 13.7118 10.6724 13.8833C10.4787 14.0549 10.2518 14.1406 9.9917 14.1406Z" />
        </svg>
      )}
      <Text
        as="p"
        variant="studio/body/medium/xs"
        lg:variant="studio/body/medium/sm"
        className="pt-[2px] text-white"
      >
        {formatTime(duration)}s selected
      </Text>
    </div>
  );
};
